"use client";

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type RefObject,
} from "react";

type Variant = "up" | "fade" | "mask" | "wipe";

/**
 * Shared IntersectionObserver hook for the motion primitives.
 * Returns [ref, inView]. All styling is CSS-driven off data attributes
 * gated behind the pre-hydration `.js` class, so no-JS clients and
 * crawlers always see complete, static content.
 */
export function useInView<T extends HTMLElement>(
  options: {
    threshold?: number;
    rootMargin?: string;
    once?: boolean;
    /** Skip observation entirely (the caller already treats it as in view). */
    disabled?: boolean;
  } = {}
): [RefObject<T | null>, boolean] {
  const {
    threshold = 0.15,
    rootMargin = "0px 0px -8% 0px",
    once = true,
    disabled = false,
  } = options;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Disarm the CSS reveal failsafe (globals.css): once any motion
    // primitive has hydrated, scroll-triggered reveals are in charge.
    document.documentElement.classList.add("js-hydrated");
    if (disabled) return;
    // Clamp the threshold for very tall targets: a wrapper taller than
    // ~1/threshold viewports can never reach the requested visibility
    // ratio, which deadlocks the observer (July audit blocker on the
    // Journal grid). Cap the required intersection at half a viewport.
    const effectiveThreshold =
      el.offsetHeight > 0
        ? Math.min(threshold, (window.innerHeight * 0.5) / el.offsetHeight)
        : threshold;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold: effectiveThreshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin, once, disabled]);

  return [ref, inView];
}

interface RevealProps extends HTMLAttributes<HTMLDivElement> {
  delay?: number;
  threshold?: number;
  /**
   * up: fade and rise 28px (default).
   * fade: opacity only.
   * mask: clip from the top edge.
   * wipe: SI-02 blind-wipe; clip-path opens bottom-to-top while any
   *       image inside settles from scale(1.06) to 1. Use for
   *       editorial images: <Reveal variant="wipe"><Image ... /></Reveal>
   */
  variant?: Variant;
  once?: boolean;
  /**
   * Above-the-fold opt-out. The element renders already revealed in the
   * server HTML (data-reveal-in="true"), so its content is painted and
   * measurable before any JS runs: no scroll observer, no hidden initial
   * state, nothing for the LCP to wait on. Use only for content that is
   * guaranteed in the first viewport, e.g. the homepage hero.
   */
  immediate?: boolean;
}

export function Reveal({
  children,
  delay = 0,
  threshold = 0.15,
  variant = "up",
  once = true,
  immediate = false,
  className = "",
  style,
  ...rest
}: RevealProps) {
  const [ref, inView] = useInView<HTMLDivElement>({
    threshold: variant === "wipe" ? Math.max(threshold, 0.2) : threshold,
    once,
    disabled: immediate,
  });
  const visible = immediate || inView;

  const composedStyle: CSSProperties = {
    transitionDelay: `${delay}ms`,
    ...style,
  };

  return (
    <div
      ref={ref}
      data-reveal={variant}
      data-reveal-in={visible ? "true" : "false"}
      className={className}
      style={composedStyle}
      {...rest}
    >
      {variant === "wipe" ? (
        // The clip-path lives on this inner wrapper, never on the observed
        // element itself: Chromium computes zero intersection for a fully
        // clipped IO target, which deadlocks the reveal (July audit
        // blocker). CSS: .js [data-reveal="wipe"] .silk-wipe-inner.
        <div
          className="silk-wipe-inner"
          style={{ transitionDelay: `${delay}ms` }}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
